import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, Square, Loader2, Radio, Info, Languages } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_MS = 5000;

/** Public sandbox so anyone can test the live AI voice→text translation that
 *  runs inside the real tutor video room — no booking or payment needed. */
const TranslateDemo = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [error, setError] = useState("");
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const sendChunk = async (blob) => {
    if (!blob || blob.size < 1000) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "chunk.webm");
      fd.append("target_lang", "English");
      const r = await axios.post(`${API}/pronunciation/translate`, fd);
      if (r.data?.transcript || r.data?.translation) {
        setCaptions((c) => [...c, { ar: r.data.transcript, en: r.data.translation, t: new Date().toISOString() }].slice(-30));
      }
    } catch (e) { console.debug("demo chunk skipped:", e); }
    finally { setBusy(false); }
  };

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mr = null;
      const rotate = () => {
        if (mr && mr.state !== "inactive") mr.stop();
        mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
        recorderRef.current = mr;
        const chunks = [];
        mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        mr.onstop = () => sendChunk(new Blob(chunks, { type: "audio/webm" }));
        mr.start();
      };
      rotate();
      timerRef.current = setInterval(rotate, CHUNK_MS);
      setOn(true);
    } catch (e) {
      setError("Microphone access denied. Please allow the microphone to test translation.");
    }
  };
  const stop = () => {
    setOn(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  return (
    <div data-testid="translate-demo-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
        <span>/</span><span className="text-forest"><Tx>AI translation demo</Tx></span>
      </div>

      <div className="text-center mb-8">
        <div className="w-12 h-12 rounded-full bg-gold/15 text-gold flex items-center justify-center mx-auto mb-3"><Languages className="w-6 h-6" /></div>
        <h1 className="font-heading text-3xl sm:text-4xl text-forest font-bold mb-3"><Tx>Live AI Translation — test it now</Tx></h1>
        <p className="text-sm text-mute leading-relaxed max-w-xl mx-auto">
          <Tx>This is the exact real-time translation used inside live tutor sessions. Press Start and speak Arabic (or any language) — it transcribes and translates to English captions within a few seconds.</Tx>
        </p>
      </div>

      <div className="clay-surface rounded-3xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Radio className={`w-4 h-4 ${on ? "text-clay animate-pulse" : "text-gold"}`} />
            <h3 className="font-heading text-forest font-semibold text-sm"><Tx>Live captions (→ EN)</Tx></h3>
          </div>
          {!on ? (
            <button data-testid="demo-start" onClick={start} className="text-xs inline-flex items-center gap-1 bg-forest text-sand px-4 py-2 rounded-full hover:bg-sage">
              <Mic className="w-3.5 h-3.5" /> <Tx>Start</Tx>
            </button>
          ) : (
            <button data-testid="demo-stop" onClick={stop} className="text-xs inline-flex items-center gap-1 bg-clay text-sand px-4 py-2 rounded-full">
              <Square className="w-3.5 h-3.5 fill-current" /> <Tx>Stop</Tx>
            </button>
          )}
        </div>

        <div className="flex items-start gap-2 text-[11px] text-mute bg-cream rounded-2xl p-3 mb-3">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <p><Tx>Best-effort AI (OpenAI Whisper + a language model). Captions appear every few seconds. For Qur'anic terms some words may be simplified.</Tx></p>
        </div>

        <div data-testid="demo-captions" className="space-y-3 min-h-[180px]">
          {captions.length === 0 && (
            <p className="text-xs text-mute italic text-center py-10">
              {on ? <Tx>Listening… start speaking and captions will appear.</Tx> : <Tx>Press Start, allow the microphone, and begin speaking.</Tx>}
            </p>
          )}
          {captions.map((c, i) => (
            <div key={i} className="border-b border-forest/5 pb-2">
              {c.ar && <p className="arabic text-base text-forest text-right" dir="rtl">{c.ar}</p>}
              {c.en && <p className="text-xs text-ink mt-1"><em>{c.en}</em></p>}
            </div>
          ))}
          {busy && <p className="text-[10px] text-mute italic text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /><Tx>translating…</Tx></p>}
        </div>

        {error && <p className="text-xs text-clay bg-clay/10 rounded-full px-3 py-2 mt-2">{error}</p>}
      </div>

      <p className="text-center text-xs text-mute mt-6">
        <Link to="/academy/tutors" className="text-forest hover:underline"><Tx>Book a real 1-to-1 session with a tutor →</Tx></Link>
      </p>
    </div>
  );
};

export default TranslateDemo;
